import React, { useState, useEffect } from 'react';
import { Row, Col, Card, CardBody, CardHeader, FormGroup, FormFeedback, Button } from 'reactstrap';
import { LabelText } from '../Utils/Input';
import InputField from '../Utils/InputField';
import SelectField from '../Utils/SelectField';
import { ImageUpload } from '../Utils/ImageUpload'; // Import the common component
import { handleChangeForCity, handleChangeForCountry, handleChangeForState } from "../Customers/constants/HandleChangeFunctions";
import { TypeOptions } from './Constants/PartnerUtils';
import { CreatableSelectField } from '../Utils/CreatableSelectField';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

const BusinessProfileForm = ({
    handleChange,
    handleBlur,
    values,
    touched,
    setFileSelected,
    errors,
    setFieldValue,
    initialValues,
    options,
    setSelectedCountry,
    setFieldTouched,
    setSelectedState,
    setSelectedCity,
    setCities,
    optionState,
    optionCity,
    setLogoFile, 
    setImageOn,
    fileInputRef,
    isCreate,
    handleRemoveLogo,
    logoFile,
    profile,
    imageOn,
    dispatch,
    setLoading,
    setUpdate,
    documentName,
    PartnerId,
    isEdit,
    setDialCode,
    dialCode,
    setOnFocus,
    onfocus,
    submitCount
}) => {
    const [phoneError, setPhoneError] = useState("");
    const [logoSizeError, setLogoSizeError] = useState(""); // Add state for logo size validation

    useEffect(() => {
        if (touched.mobile || submitCount > 0) {
            validatePhone(dialCode);
        }
    }, [dialCode, touched.mobile, submitCount]);

    const validatePhone = (phoneValue) => {
        if (!phoneValue) {
            setPhoneError("Phone number is required");
        } else if (!isValidPhoneNumber(phoneValue)) {
            setPhoneError("Please enter a valid phone number");
        } else {
            setPhoneError("");
        }
    };

    const handlePhoneFocus = () => {
        setOnFocus(true);
        validatePhone(dialCode);
    };

    const handlePhoneBlur = () => {
        validatePhone(dialCode);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file size (10MB limit)
            const maxSize = 10 * 1024 * 1024; // 10MB in bytes
            if (file.size > maxSize) {
                setLogoSizeError("File size must be less than 10MB");
                return;
            }
            
            setLogoSizeError("");
            setLogoFile(file);
            setImageOn(true);
            setFileSelected(true);
        } else {
            setLogoFile(null);
            setImageOn(false);
            setFileSelected(false);
        }
    };

    const handleLogoRemove = () => {
        handleRemoveLogo(dispatch, setLoading, setLogoFile, setImageOn, setUpdate, fileInputRef, documentName, PartnerId, isEdit);
        setLogoSizeError(""); // Clear any size errors when removing
    };

    return (
        <Row className='p-2'>
            <Col lg={12}>
                <CardBody className="card-body">
                    <div className="live-preview">
                        <Row className="gy-1 border-1 border p-2">
                            <h4 className="card-title flex-grow-1 mt-2">Business Profile</h4>
                            
                            <Col xxl={3}>
                                <div className='mt-2'>
                                    <ImageUpload
                                        validateSizeError={logoSizeError}
                                        profilePicture={logoFile}
                                        isProfile={imageOn}
                                        inputFileRef={fileInputRef}
                                        removeProfilePicture={handleLogoRemove}
                                        handleImageChange={handleFileChange}
                                        isFromProfileScreen={false}
                                    />
                                </div>
                            </Col>
                            
                            <Col xxl={9}>
                                <Row className='mt-2'>
                                    <Col xxl={8}>
                                        <div>
                                            <FormGroup>
                                                <LabelText 
                                                    htmlFor={"businessName"} 
                                                    classLabelName={"ms-0 rv-form-label"}
                                                    className={"text-danger"} 
                                                    displayText={"Business Name"} 
                                                    important={"*"} 
                                                />
                                                <LabelText
                                                    htmlFor={"businessName"}
                                                    classLabelName={`d-block ms-0 rv-sub-label ${
                                                        touched.businessName && errors.businessName
                                                            ? "rv-label-danger"
                                                            : "rv-label-muted"
                                                    }`}
                                                    displayText={
                                                        touched.businessName && errors.businessName
                                                            ? errors.businessName
                                                            : "Enter your Business Name"
                                                    }
                                                />
                                                <InputField 
                                                    name={"businessName"} 
                                                    type={"text"} 
                                                    className={"form-control"} 
                                                    id={"businessName"} 
                                                    handleChange={handleChange} 
                                                    handleBlur={handleBlur} 
                                                    value={values.businessName}
                                                    invalid={touched.businessName && errors.businessName ? true : false} 
                                                />
                                            </FormGroup>
                                        </div>
                                    </Col>
                                    <Col xxl={4}>
                                        <div>
                                            <LabelText 
                                                htmlFor={"type"} 
                                                classLabelName={"ms-0 rv-form-label"}
                                                className={"text-danger"} 
                                                displayText={"Type"} 
                                                important={"*"} 
                                            />
                                            <LabelText
                                                htmlFor={"type"}
                                                classLabelName={`d-block ms-0 rv-sub-label ${
                                                    touched.type && errors.type
                                                        ? "rv-label-danger"
                                                        : "rv-label-muted"
                                                }`}
                                                displayText={
                                                    touched.type && errors.type
                                                        ? errors.type
                                                        : "Select Type"
                                                }
                                            />
                                            <SelectField 
                                                className={(errors.type && touched.type) ? 'error-input' : ''}
                                                value={values.type}
                                                handleChange={(selectedVal) => setFieldValue("type", selectedVal)}
                                                isClearable={true}
                                                options={TypeOptions} 
                                            />
                                        </div>
                                    </Col>
                                </Row>

                                <Row className='mt-2'>
                                    <Col xxl={4}>
                                        <div>
                                            <FormGroup>
                                                <LabelText 
                                                    htmlFor={"email"} 
                                                    classLabelName={"ms-0 rv-form-label"}
                                                    className={"text-danger"} 
                                                    displayText={"Email Address"} 
                                                    important={"*"} 
                                                />
                                                <LabelText
                                                    htmlFor={"email"}
                                                    classLabelName={`d-block ms-0 rv-sub-label ${
                                                        touched.email && errors.email
                                                            ? "rv-label-danger"
                                                            : "rv-label-muted"
                                                    }`}
                                                    displayText={
                                                        touched.email && errors.email
                                                            ? errors.email
                                                            : "Enter your Email"
                                                    }
                                                />
                                                <InputField 
                                                    name={"email"} 
                                                    type={"email"} 
                                                    className={"form-control"} 
                                                    id={"email"} 
                                                    handleChange={handleChange} 
                                                    handleBlur={handleBlur} 
                                                    value={values.email}
                                                    invalid={touched.email && errors.email ? true : false} 
                                                />
                                            </FormGroup>
                                        </div>
                                    </Col>
                                    <Col xxl={4}>
                                        <div>
                                            <FormGroup>
                                                <LabelText 
                                                    htmlFor={"mobile"} 
                                                    classLabelName={"ms-0 rv-form-label"}
                                                    className={"text-danger"} 
                                                    displayText={"Phone Number"} 
                                                    important={"*"} 
                                                />
                                                <LabelText
                                                    htmlFor={"mobile"}
                                                    classLabelName={`d-block ms-0 rv-sub-label ${
                                                        phoneError
                                                            ? "rv-label-danger"
                                                            : "rv-label-muted"
                                                    }`}
                                                    displayText={
                                                        phoneError
                                                            ? phoneError
                                                            : "Enter your Phone Number"
                                                    }
                                                />
                                                <PhoneInput
                                                    international
                                                    countryCallingCodeEditable={false}
                                                    className={`form-control ps-3 ${phoneError ? 'error-input' : ''}`}
                                                    onChange={(value) => {
                                                        setDialCode(value);
                                                        setFieldValue("phoneNumber", value);
                                                    }}
                                                    onFocus={handlePhoneFocus}
                                                    onBlur={handlePhoneBlur}
                                                    value={dialCode}
                                                />
                                            </FormGroup>
                                        </div>
                                    </Col>
                                    <Col xxl={4}>
                                        <div>
                                            <FormGroup>
                                                <LabelText 
                                                    htmlFor={"websiteLink"} 
                                                    classLabelName={"ms-0 rv-form-label"}
                                                    className={"text-danger"} 
                                                    displayText={"Website Link"} 
                                                />
                                                <LabelText
                                                    htmlFor={"websiteLink"}
                                                    classLabelName={"d-block ms-0 rv-sub-label rv-label-muted"}
                                                    displayText={"Enter your Website Link"}
                                                />
                                                <InputField 
                                                    name={"websiteLink"} 
                                                    type={"text"} 
                                                    className={"form-control"} 
                                                    id={"websiteLink"} 
                                                    handleChange={handleChange} 
                                                    handleBlur={handleBlur} 
                                                    value={values.websiteLink} 
                                                />
                                            </FormGroup>
                                        </div>
                                    </Col>
                                    
                                    <Col xxl={4} className='mb-3'>
                                        <FormGroup>
                                            <LabelText 
                                                htmlFor={"Address"} 
                                                classLabelName={"ms-0 rv-form-label"}
                                                className={"text-danger"} 
                                                displayText={"Address"} 
                                                important={"*"} 
                                            />
                                            <LabelText
                                                htmlFor={"Address"}
                                                classLabelName={`d-block ms-0 rv-sub-label ${
                                                    touched.Address && errors.Address
                                                        ? "rv-label-danger"
                                                        : "rv-label-muted"
                                                }`}
                                                displayText={
                                                    touched.Address && errors.Address
                                                        ? errors.Address
                                                        : "Enter your Address"
                                                }
                                            />
                                            <InputField 
                                                name={"Address"} 
                                                type={"text"} 
                                                className={"form-control"} 
                                                id={"Address"} 
                                                handleChange={handleChange} 
                                                handleBlur={handleBlur} 
                                                value={values.Address}
                                                invalid={touched.Address && errors.Address ? true : false} 
                                            />
                                        </FormGroup>
                                    </Col>

                                    <Col xxl={4}>
                                        <div>
                                            <LabelText 
                                                htmlFor={"addressLine2"} 
                                                classLabelName={"ms-0 rv-form-label"}
                                                className={"text-danger"} 
                                                displayText={"Address 2"} 
                                            />
                                            <LabelText
                                                htmlFor={"addressLine2"}
                                                classLabelName={"d-block ms-0 rv-sub-label rv-label-muted"}
                                                displayText={"Enter your Address 2"}
                                            />
                                            <InputField 
                                                name={"addressLine2"} 
                                                type={"text"} 
                                                className={"form-control"} 
                                                handleChange={handleChange} 
                                                id={"addressLine2"} 
                                                value={values.addressLine2} 
                                            />
                                        </div>
                                    </Col>

                                    <Col xxl={4}>
                                        <div>
                                            <LabelText 
                                                htmlFor={"country"} 
                                                classLabelName={"ms-0 rv-form-label"}
                                                className={"text-danger"} 
                                                displayText={"Country"} 
                                                important={"*"} 
                                            />
                                            <LabelText
                                                htmlFor={"country"}
                                                classLabelName={`d-block ms-0 rv-sub-label ${
                                                    touched.country && errors.country
                                                        ? "rv-label-danger"
                                                        : "rv-label-muted"
                                                }`}
                                                displayText={
                                                    touched.country && errors.country
                                                        ? errors.country
                                                        : "Select your Country"
                                                }
                                            />
                                            <SelectField 
                                                className={(errors.country && touched.country) ? 'error-input' : ''}
                                                value={values.country} 
                                                handleChange={handleChangeForCountry(setSelectedCountry, setFieldValue, setFieldTouched, setSelectedState, setSelectedCity, setCities)}
                                                isClearable={true}
                                                options={options} 
                                            />
                                        </div>
                                    </Col>

                                    <Col xxl={4}>
                                        <div>
                                            <FormGroup>
                                                <LabelText 
                                                    htmlFor={"state"} 
                                                    classLabelName={"ms-0 rv-form-label"}
                                                    className={"text-danger"} 
                                                    displayText={"State"} 
                                                    important={"*"} 
                                                />
                                                <LabelText
                                                    htmlFor={"state"}
                                                    classLabelName={`d-block ms-0 rv-sub-label ${
                                                        touched.state && errors.state
                                                            ? "rv-label-danger"
                                                            : "rv-label-muted"
                                                    }`}
                                                    displayText={
                                                        touched.state && errors.state
                                                            ? errors.state
                                                            : "Select your State"
                                                    }
                                                />
                                                <SelectField
                                                    className={(errors.state && touched.state) ? 'error-input' : ''}
                                                    value={values.state}
                                                    handleChange={handleChangeForState(setSelectedState, setFieldValue)}
                                                    options={optionState}
                                                    isClearable={true}
                                                    isDisabled={!values.country}
                                                />
                                            </FormGroup>
                                        </div>
                                    </Col>

                                    <Col xxl={4}>
                                        <div>
                                            <LabelText 
                                                htmlFor={"city"} 
                                                classLabelName={"ms-0 rv-form-label"}
                                                className={"text-danger"} 
                                                displayText={"City"} 
                                                important={"*"} 
                                            />
                                            <LabelText
                                                htmlFor={"city"}
                                                classLabelName={`d-block ms-0 rv-sub-label ${
                                                    touched.city && errors.city
                                                        ? "rv-label-danger"
                                                        : "rv-label-muted"
                                                }`}
                                                displayText={
                                                    touched.city && errors.city
                                                        ? errors.city
                                                        : "Select your city"
                                                }
                                            />
                                            <CreatableSelectField 
                                                values={values.city} 
                                                handleChange={(value) => {
                                                    setFieldValue(`city`, value, false);
                                                    setFieldTouched(`city`, false);
                                                }}
                                                className={(errors.city && touched.city) ? 'error-input' : ''}
                                                onKeyDown={(event) => {
                                                    const key = event.key;
                                                    const regex = /^[a-zA-Z0-9]$/;
                                                    if (regex.test(key) || ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Enter'].includes(key)) {
                                                        return;
                                                    }
                                                    event.preventDefault();
                                                }}
                                                name={"city"} 
                                                options={optionCity}
                                                fieldName={"city"}
                                                setFieldValue={setFieldValue}
                                                isClearable={true}
                                                isDisabled={!values.state}
                                            />
                                        </div>
                                    </Col>

                                    <Col xxl={4}>
                                        <div>
                                            <FormGroup>
                                                <LabelText 
                                                    htmlFor={"zipCode"} 
                                                    classLabelName={"ms-0 rv-form-label"}
                                                    className={"text-danger"} 
                                                    displayText={"Zip Code"} 
                                                    important={"*"} 
                                                />
                                                <LabelText
                                                    htmlFor={"zipCode"}
                                                    classLabelName={`d-block ms-0 rv-sub-label ${
                                                        touched.zipCode && errors.zipCode
                                                            ? "rv-label-danger"
                                                            : "rv-label-muted"
                                                    }`}
                                                    displayText={
                                                        touched.zipCode && errors.zipCode
                                                            ? errors.zipCode
                                                            : "Enter your Zip Code"
                                                    }
                                                />
                                                <InputField 
                                                    name={"zipCode"} 
                                                    type={"number"} 
                                                    className={"form-control"} 
                                                    id={"zipCode"} 
                                                    handleChange={handleChange} 
                                                    handleBlur={handleBlur} 
                                                    value={values.zipCode}
                                                    invalid={touched.zipCode && errors.zipCode ? true : false} 
                                                />
                                            </FormGroup>
                                        </div>
                                    </Col>
                                </Row>
                            </Col>                        
                        </Row>
                    </div>
                </CardBody>
            </Col>
        </Row>
    );
};

export default BusinessProfileForm;